// Do not put any dispatcher here.
// Only pure API calls
// The Facades should mimic exactly the corresponding API.

import { useCallback } from 'react';
import { useSweepApi } from '../sweep';
import buildURLQuery from '../../lib/buildURLQuery';
import { CrmOrg } from '../../types/CrmOrgs';
import {
  CrmOrgCreateDto,
  CrmOrgPatchDto,
  CrmOrgGetUsersResponse,
  CrmOrgUsersBaseResponse,
  CrmOrgOAuthDto,
  CrmOrgEmailTemplatesResponse,
  CrmOrgQueueResponse,
  ObjectTypesArguments,
  CrmOrgObjectTypesResponse,
  ChildRelationshipObjectResponse,
  SFDCLeadingField,
  SnowflakeCredentials,
  SnowflakeDatabase,
  SnowflakeOrgCreateDto,
  ParsedRecordTypesAndFunnels,
  ParsedRecordTypesAndFunnelsObjectData,
} from './types/crmOrgsApiFacade.types';

type SFDCObjectType = 'Lead' | 'Account' | 'Opportunity' | 'Contact' | 'Case';

export const useCrmOrgsApiFacade = () => {
  const sweepApi = useSweepApi();

  return {
    get_crmOrgs: useCallback(
      async ({ connectedOnly }: { connectedOnly?: boolean } = {}) => {
        const { data } = await sweepApi.get<CrmOrg[]>(
          `/crm-orgs${connectedOnly ? '?connectedOnly=true' : ''}`,
        );

        return data;
      },
      [sweepApi],
    ),
    get_crmOrg: useCallback(
      async ({ orgId }: { orgId: string }) => {
        const response = await sweepApi.get(`/crm-orgs/org/${orgId}`);

        return response.data as CrmOrg;
      },
      [sweepApi],
    ),
    delete_org: useCallback(
      async ({ orgId }: { orgId: string }) => {
        const response = await sweepApi.delete(`/orgs/${orgId}`);

        return response.data;
      },
      [sweepApi],
    ),
    post_crmOrg: useCallback(
      async ({ payload: { name, platform, isMain, isSandbox } }: { payload: CrmOrgCreateDto }) => {
        const response = await sweepApi.post(`/crm-orgs/new`, {
          name,
          platform,
          isMain,
          isSandbox,
        });

        return response.data as CrmOrg;
      },
      [sweepApi],
    ),
    patch_crmOrg: useCallback(
      async ({ orgId, payload: { name } }: { orgId: string; payload: CrmOrgPatchDto }) => {
        const response = await sweepApi.patch(`/crm-orgs/org/${orgId}`, { name });

        return response.data as CrmOrg;
      },
      [sweepApi],
    ),
    get_crmOrgUsers: useCallback(
      async ({ orgId, includeInactive }: { orgId: string; includeInactive?: boolean }) => {
        const response = await sweepApi.get(
          `/crm-orgs/org/${orgId}/users/member-properties` +
            (includeInactive ? '?includeInactive=true' : ''),
        );

        return response.data as CrmOrgGetUsersResponse;
      },
      [sweepApi],
    ),
    get_crmOrgUsersBase: useCallback(
      async ({ orgId, includeInactive }: { orgId: string; includeInactive?: boolean }) => {
        const response = await sweepApi.get(
          `/crm-orgs/org/${orgId}/users` + (includeInactive ? '?includeInactive=true' : ''),
        );

        return response.data as CrmOrgUsersBaseResponse;
      },
      [sweepApi],
    ),

    post_crmOrgSalesforceOauthCode: useCallback(
      async ({
        payload: { crmOrgId, authCode, isSandbox, isOnboardingFastFetch },
        queryParams,
      }: {
        payload: CrmOrgOAuthDto;
        queryParams?: {
          isProcessMiningEnabled: boolean;
          force?: boolean;
        };
      }) => {
        const query = buildURLQuery({
          isProcessMiningEnabled: queryParams?.isProcessMiningEnabled,
          force: queryParams?.force,
        });
        const response = await sweepApi.post(`/crm-orgs/salesforce-oauth2-code?${query}`, {
          crmOrgId,
          authCode,
          isSandbox,
          isOnboardingFastFetch,
        });

        return response.data;
      },
      [sweepApi],
    ),

    post_orgFetch: useCallback(
      async ({ orgId, withReset }: { orgId: string; withReset: boolean }) => {
        const addReset = withReset === true ? '?factory-reset=true' : '';
        const response = await sweepApi.post(`/orgs/${orgId}/fetch${addReset}`);

        return response.data;
      },
      [sweepApi],
    ),

    get_crmOrgEmailTemplates: useCallback(
      async ({ orgId }: { orgId: string }) => {
        const response = await sweepApi.get(`/crm-orgs/org/${orgId}/emailTemplates`);

        return response.data as CrmOrgEmailTemplatesResponse;
      },
      [sweepApi],
    ),

    get_crmOrgsQueues: useCallback(
      async ({ orgId, objectType }: { orgId: string; objectType: string }) => {
        const response = await sweepApi.get(
          `/crm-orgs/org/${orgId}/queues?objectType=${objectType}`,
        );

        return response.data as CrmOrgQueueResponse;
      },
      [sweepApi],
    ),

    get_crmOrgsObjectTypes: useCallback(
      async ({
        orgId,
        createableObjectsOnly,
        triggerableObjectsOnly,
        updateableObjectsOnly,
        queryableObjectsOnly,
      }: ObjectTypesArguments) => {
        const query = buildURLQuery({
          createableObjectsOnly,
          triggerableObjectsOnly,
          updateableObjectsOnly,
          queryableObjectsOnly,
        });
        const response = await sweepApi.get<CrmOrgObjectTypesResponse>(
          `/crm-orgs/org/${orgId}/object-types-2` + (query ? `?${query}` : ''),
        );

        return response.data;
      },
      [sweepApi],
    ),

    get_crmOrgsObjectTypesChildRelationshipNames: useCallback(
      async ({ orgId, objectType }: { orgId: string; objectType: SFDCObjectType }) => {
        const response = await sweepApi.get(
          `/crm-orgs/org/${orgId}/object-types/${objectType}/child-relationship-names`,
        );

        return response.data as ChildRelationshipObjectResponse;
      },
      [sweepApi],
    ),
    get_recordTypesAndFunnels: useCallback(
      async ({ crmOrgId }: { crmOrgId: string }) => {
        const response = await sweepApi.get(`/crm-orgs/org/${crmOrgId}/funnels-and-record-types-2`);
        return response.data as ParsedRecordTypesAndFunnels;
      },
      [sweepApi],
    ),
    getObjectFunnelsAndRecordTypes: useCallback(
      async ({ crmOrgId, objectName }: { crmOrgId: string; objectName: string }) => {
        const response = await sweepApi.get(
          `/crm-orgs/org/${crmOrgId}/objects/${objectName}/funnels-and-record-types`,
        );
        return response.data as ParsedRecordTypesAndFunnelsObjectData;
      },
      [sweepApi],
    ),
    // This call uses Salesforce
    get_objectRecordType: useCallback(
      async ({
        crmOrgId,
        recordTypeName,
        objectApiName,
      }: {
        crmOrgId: string;
        recordTypeName: string;
        objectApiName: string;
      }) => {
        const response = await sweepApi.get(
          `/crm-orgs/org/${crmOrgId}/objects/${objectApiName}/record-types/${recordTypeName}`,
        );
        return response.data as SFDCLeadingField[];
      },
      [sweepApi],
    ),

    post_userTimeOff: useCallback(
      async ({
        crmOrgId,
        timeOff,
        userId,
      }: {
        crmOrgId: string;
        userId: string;
        timeOff: CrmOrgUserTimeOff;
      }) => {
        await sweepApi.post(`/crm-orgs/org/${crmOrgId}/users/${userId}/timeOff`, timeOff);
      },
      [sweepApi],
    ),
    put_userTimeOff: useCallback(
      async ({
        crmOrgId,
        timeOff,
        userId,
      }: {
        crmOrgId: string;
        userId: string;
        timeOff: CrmOrgUserTimeOff;
      }) => {
        await sweepApi.put(
          `/crm-orgs/org/${crmOrgId}/users/${userId}/timeOff/${timeOff.id}`,
          timeOff,
        );
      },
      [sweepApi],
    ),
    delete_userTimeOff: useCallback(
      async ({
        crmOrgId,
        timeOffId,
        userId,
      }: {
        crmOrgId: string;
        userId: string;
        timeOffId: string;
      }) => {
        await sweepApi.delete(`/crm-orgs/org/${crmOrgId}/users/${userId}/timeOff/${timeOffId}`);
      },
      [sweepApi],
    ),
    get_crmOrgSalesforceOAuthUrl: useCallback(
      async ({ crmOrgId }: { crmOrgId: string }) => {
        const response = await sweepApi.get(`/crm-orgs/org/${crmOrgId}/salesforce-oauth`);
        return response.data as { url: string };
      },
      [sweepApi],
    ),
    post_assignPermissionSetGroup: useCallback(
      async ({ crmOrgId, assignToAll }: { crmOrgId: string; assignToAll: boolean }) => {
        await sweepApi.post(
          `/crm-orgs/org/${crmOrgId}/sweep-permission-set-group-assignment?all=${assignToAll}`,
        );
      },
      [sweepApi],
    ),
    get_snowflakeDatabases: useCallback(
      async (payload: SnowflakeCredentials) => {
        const response = await sweepApi.post('/snowflake-orgs/databases', { credentials: payload });
        return response.data as SnowflakeDatabase[];
      },
      [sweepApi],
    ),
    post_snowflakeOrg: useCallback(
      async (payload: SnowflakeOrgCreateDto) => {
        const response = await sweepApi.post('/snowflake-orgs', payload);
        return response.data as CrmOrg;
      },
      [sweepApi],
    ),
    get_orgsAll: useCallback(
      async ({ connectedOnly }: { connectedOnly?: boolean } = {}) => {
        const { data } = await sweepApi.get<CrmOrg[]>(
          `/orgs${connectedOnly ? '?connectedOnly=true' : ''}`,
        );
        return data;
      },
      [sweepApi],
    ),
  };
};
